import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../lib/api'
import { VehicleAvatar } from '../components/VehicleCard'

export default function AddVehiclePage() {
  const navigate = useNavigate()

  useEffect(() => {
    api.get('/dashboard').then((res) => {
      const { premium, vehicle_limit, total_vehicles } = res.data
      if (!premium && total_vehicles >= vehicle_limit) {
        navigate('/dashboard', { replace: true })
      }
    })
  }, [])

  const [vin, setVin] = useState('')
  const [decoding, setDecoding] = useState(false)
  const [form, setForm] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    vin: '',
    body_class: '',
    engine_model: '',
    current_meter: '',
    purchase_meter: '',
    purchase_date: '',
    meter_type: 'miles',
  })
  const [photo, setPhoto] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)
  const photoInputRef = useRef(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setPhoto(file)
    setPhotoPreview(URL.createObjectURL(file))
  }

  const handleRemovePhoto = () => {
    setPhoto(null)
    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhotoPreview(null)
    if (photoInputRef.current) photoInputRef.current.value = ''
  }

  const handleDecode = async () => {
    if (!vin.trim()) return
    setDecoding(true)
    setError(null)

    try {
      const res = await api.get('/vin_lookup', { params: { vin: vin.trim() } })
      setForm((prev) => ({
        ...prev,
        vin: vin.trim().toUpperCase(),
        year: res.data.year || '',
        make: res.data.make || '',
        model: res.data.model || '',
        trim: res.data.trim || '',
        body_class: res.data.body_class || '',
        engine_model: res.data.engine_model || '',
      }))
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to decode VIN')
    } finally {
      setDecoding(false)
    }
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const isFieldMissing = (val) => submitted && !val

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitted(true)

    if (!form.year || !form.make || !form.model) return

    setSaving(true)
    setError(null)

    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        current_meter: form.current_meter ? parseInt(form.current_meter) : null,
        purchase_meter: form.purchase_meter ? parseInt(form.purchase_meter) : null,
        purchase_date: form.purchase_date || null,
      }

      let res
      if (photo) {
        const formData = new FormData()
        for (const [key, value] of Object.entries(payload)) {
          if (value != null) formData.append(`vehicle[${key}]`, value)
        }
        formData.append('vehicle[photo]', photo)
        res = await api.post('/vehicles', formData)
      } else {
        res = await api.post('/vehicles', { vehicle: payload })
      }
      navigate(`/vehicles/${res.data.id}`)
    } catch (err) {
      setError(err.response?.data?.errors?.join(', ') || 'Failed to save vehicle')
    } finally {
      setSaving(false)
    }
  }

  const unitLabel = form.meter_type === 'km' ? 'km' : form.meter_type === 'hours' ? 'hrs' : 'mi'

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-ink mb-6">Add Vehicle</h1>

      <div className="bg-surface border-[1.5px] border-ink p-6 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">VIN Lookup (optional)</h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, ''))}
            placeholder="Enter 17-character VIN"
            maxLength={17}
            className="flex-1 border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm font-mono focus:ring-2 focus:ring-accent focus:border-accent"
          />
          <button
            onClick={handleDecode}
            disabled={decoding || vin.length !== 17}
            className="px-4 py-2 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark disabled:opacity-50"
          >
            {decoding ? 'Decoding...' : 'Decode'}
          </button>
        </div>
        {vin.length > 0 && vin.length < 17 && (
          <p className="text-xs text-accent-dark mt-1">VIN must be exactly 17 characters ({vin.length}/17). Letters I, O, and Q are not used in VINs.</p>
        )}
      </div>

      {error && (
        <div className="text-sm text-accent-dark bg-accent/10 border border-accent/40 p-3 mb-6">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="bg-surface border-[1.5px] border-ink p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Photo</label>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoSelect}
          />
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              title="Click to select photo"
            >
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Vehicle photo"
                  className="rounded-full object-cover"
                  style={{ width: 56, height: 56 }}
                />
              ) : (
                <VehicleAvatar vehicle={{ model: form.model || '?' }} size={56} />
              )}
            </button>
            <div className="text-sm text-gray-500">
              {photo ? (
                <div className="flex items-center gap-2">
                  <span className="text-gray-900">{photo.name}</span>
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="text-xs text-accent-dark hover:text-accent"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <span>Click to add a photo</span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
            <input
              name="year"
              type="number"
              value={form.year}
              onChange={handleChange}
              className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(form.year) ? 'border-accent' : ''}`}
            />
            {isFieldMissing(form.year) && <p className="text-xs text-accent-dark mt-1">Required</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Make *</label>
            <input
              name="make"
              value={form.make}
              onChange={handleChange}
              className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(form.make) ? 'border-accent' : ''}`}
            />
            {isFieldMissing(form.make) && <p className="text-xs text-accent-dark mt-1">Required</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
            <input
              name="model"
              value={form.model}
              onChange={handleChange}
              className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(form.model) ? 'border-accent' : ''}`}
            />
            {isFieldMissing(form.model) && <p className="text-xs text-accent-dark mt-1">Required</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trim</label>
            <input
              name="trim"
              value={form.trim}
              onChange={handleChange}
              className="w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
            <input
              name="vin"
              value={form.vin}
              onChange={handleChange}
              className="w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm font-mono focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body Class</label>
            <input
              name="body_class"
              value={form.body_class}
              onChange={handleChange}
              className="w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Engine</label>
          <input
            name="engine_model"
            value={form.engine_model}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end overflow-hidden">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meter Unit</label>
            <select
              name="meter_type"
              value={form.meter_type}
              onChange={handleChange}
              className="w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"
            >
              <option value="miles">Miles</option>
              <option value="km">Kilometers</option>
              <option value="hours">Hours</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current ({unitLabel})</label>
            <input
              name="current_meter"
              type="number"
              value={form.current_meter}
              onChange={handleChange}
              className="w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase ({unitLabel})</label>
            <input
              name="purchase_meter"
              type="number"
              value={form.purchase_meter}
              onChange={handleChange}
              className="w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>
          <div className="min-w-0 overflow-hidden">
            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
            <input
              name="purchase_date"
              type="date"
              value={form.purchase_date}
              onChange={handleChange}
              className="w-full min-w-0 border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Add Vehicle'}
          </button>
        </div>
      </form>
    </div>
  )
}
